import {
  ChangeDetectionStrategy, Component, computed, effect, input, output, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { ISablon, ISablonElement, ElementTur } from '../sablon-editoru/sablon.interface';

interface ISatir {
  _idx: number;
  _duzenle: boolean;
  _taslak: Record<string, any>;
  _yeni: boolean;
  veri: Record<string, any>;
}

interface ISutun {
  anahtar: string;
  baslik: string;
  tur: 'text' | 'number' | 'color';
  element: ISablonElement;
  genislik: string;
}

const SAYI_TURLER: ElementTur[] = [
  'sayac', 'ilerleme-cubugu', 'ilerleme-halka', 'gauge',
  'led-gosterge', 'slider', 'sparkline', 'bar-grafik',
  'pasta-grafik', 'alan-grafik', 'radar-grafik',
];

const RENK_ANAHTAR = ['renk', 'color', 'fill', 'stroke', 'background', 'arka'];

@Component({
  selector: 'te-parametre-tablosu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, FormsModule,
    NzTableModule, NzButtonModule, NzIconModule,
    NzInputModule, NzInputNumberModule,
    NzTagModule, NzTooltipModule, NzPopconfirmModule,
    NzBadgeModule, NzEmptyModule, NzDividerModule,
  ],
  template: `
    <div class="pt-wrapper">
      <div class="pt-header">
        <div class="pt-header-sol">
          <span nz-icon nzType="table" nzTheme="outline" class="pt-header-ikon"></span>
          <span class="pt-baslik">Parametre Tablosu</span>
          @if (sablon()?.baslik) {
            <nz-tag nzColor="blue" class="pt-sablon-tag">{{ sablon()?.baslik }}</nz-tag>
          }
          @if (satirlar().length > 0) {
            <nz-badge [nzCount]="satirlar().length"
              [nzStyle]="{ backgroundColor: '#52c41a', fontSize: '10px', height: '16px', lineHeight: '16px', minWidth: '16px', padding: '0 5px' }">
            </nz-badge>
          }
        </div>
        <div class="pt-header-sag">
          @if (sutunlar().length > 0) {
            <button nz-button nzType="dashed" nzSize="small" (click)="satirEkle()"
              nz-tooltip nzTooltipTitle="Yeni satir ekle">
              <span nz-icon nzType="plus"></span> Satir Ekle
            </button>
          }
        </div>
      </div>

      @if (sutunlar().length === 0) {
        <div class="pt-bos-durum">
          <nz-empty nzNotFoundImage="simple" nzNotFoundContent="Sablonda parametreAnahtari tanimli element bulunamadi."></nz-empty>
        </div>
      } @else {
        <div class="pt-sutun-bilgi">
          @for (s of sutunlar(); track s.anahtar) {
            <nz-tag [nzColor]="tipRengi(s.tur)" class="pt-sutun-chip">
              <span nz-icon [nzType]="tipIkon(s.tur)" nzTheme="outline"></span>
              {{ s.anahtar }}
            </nz-tag>
          }
        </div>

        <nz-table #tablo [nzData]="satirlar()" [nzBordered]="true" nzSize="small"
          [nzScroll]="{ x: scrollX(), y: '360px' }"
          [nzShowPagination]="satirlar().length > 25" [nzPageSize]="25"
          nzTableLayout="fixed" class="pt-tablo">
          <thead><tr>
            <th nzWidth="44px" nzLeft class="pt-th-no">#</th>
            @for (s of sutunlar(); track s.anahtar) {
              <th [nzWidth]="s.genislik" class="pt-th-sutun">
                <div class="pt-th-icerik">
                  <span class="pt-th-etiket" [title]="s.anahtar">{{ s.anahtar }}</span>
                  <nz-tag [nzColor]="tipRengi(s.tur)" class="pt-th-tip">
                    <span nz-icon [nzType]="tipIkon(s.tur)" nzTheme="outline"></span>
                  </nz-tag>
                </div>
              </th>
            }
            <th nzWidth="88px" nzRight class="pt-th-islem">Islem</th>
          </tr></thead>
          <tbody>
            @for (satir of tablo.data; track satir._idx; let i = $index) {
              <tr [class.pt-satir-duzenle]="satir._duzenle" [class.pt-satir-yeni]="satir._yeni && satir._duzenle">
                <td nzLeft class="pt-td-no"><span class="pt-satir-no">{{ i + 1 }}</span></td>
                @for (s of sutunlar(); track s.anahtar) {
                  <td class="pt-td-hucre">
                    @if (satir._duzenle) {
                      @if (s.tur === 'number') {
                        <nz-input-number [(ngModel)]="satir._taslak[s.anahtar]" nzSize="small" [nzStep]="1" class="pt-input-tam"></nz-input-number>
                      } @else if (s.tur === 'color') {
                        <div class="pt-renk-grup">
                          <input type="color" [(ngModel)]="satir._taslak[s.anahtar]" class="pt-renk-picker">
                          <input nz-input nzSize="small" [(ngModel)]="satir._taslak[s.anahtar]" class="pt-renk-metin" placeholder="#hex">
                        </div>
                      } @else {
                        <input nz-input nzSize="small" [(ngModel)]="satir._taslak[s.anahtar]" class="pt-input-tam"
                          [placeholder]="s.anahtar" (keydown.enter)="kaydet(satir)" (keydown.escape)="iptal(satir)">
                      }
                    } @else {
                      <div class="pt-hucre-goruntu">
                        @if (s.tur === 'color' && satir.veri[s.anahtar]) {
                          <span class="pt-renk-ornek" [style.background]="satir.veri[s.anahtar]"></span>
                        }
                        <span class="pt-hucre-deger" [class.pt-deger-bos]="satir.veri[s.anahtar] == null || satir.veri[s.anahtar] === ''">
                          {{ satir.veri[s.anahtar] ?? 'â€”' }}
                        </span>
                      </div>
                    }
                  </td>
                }
                <td nzRight class="pt-td-islem">
                  @if (satir._duzenle) {
                    <div class="pt-islem-grup">
                      <button nz-button nzType="link" nzSize="small" nz-tooltip nzTooltipTitle="Kaydet" (click)="kaydet(satir)" class="pt-btn-kaydet">
                        <span nz-icon nzType="check"></span>
                      </button>
                      <nz-divider nzType="vertical"></nz-divider>
                      <button nz-button nzType="link" nzSize="small" nz-tooltip nzTooltipTitle="Iptal" (click)="iptal(satir)" class="pt-btn-iptal">
                        <span nz-icon nzType="close"></span>
                      </button>
                    </div>
                  } @else {
                    <div class="pt-islem-grup">
                      <button nz-button nzType="link" nzSize="small" nz-tooltip nzTooltipTitle="Duzenle" (click)="duzenle(satir)" class="pt-btn-duzenle">
                        <span nz-icon nzType="edit"></span>
                      </button>
                      <nz-divider nzType="vertical"></nz-divider>
                      <button nz-button nzType="link" nzSize="small" nz-tooltip nzTooltipTitle="Sil"
                        nz-popconfirm nzPopconfirmTitle="Bu satiri silmek istediginize emin misiniz?"
                        nzPopconfirmPlacement="left" (nzOnConfirm)="sil(satir._idx)" class="pt-btn-sil">
                        <span nz-icon nzType="delete"></span>
                      </button>
                    </div>
                  }
                </td>
              </tr>
            }
            @if (satirlar().length === 0) {
              <tr><td [attr.colspan]="sutunlar().length + 2" class="pt-td-bos">
                <nz-empty nzNotFoundImage="simple" nzNotFoundContent="Henuz satir eklenmedi"></nz-empty>
              </td></tr>
            }
          </tbody>
        </nz-table>

        <div class="pt-alt-bar">
          <span class="pt-alt-bilgi">
            <span nz-icon nzType="info-circle" nzTheme="outline"></span>
            <strong>Enter</strong> kaydet &bull; <strong>Esc</strong> iptal
          </span>
          <span class="pt-alt-sayac">{{ satirlar().length }} satir &bull; {{ sutunlar().length }} sutun</span>
        </div>
      }
    </div>
  `,
  styles: `
    :host { display: flex; flex: 1; min-height: 0; }
    .pt-wrapper { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--pt-bg, #fff); border: 1px solid var(--pt-border, rgba(0,0,0,0.08)); border-radius: 8px; overflow: hidden; }
    .pt-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; flex-shrink: 0; background: var(--pt-header-bg, #fafafa); border-bottom: 1px solid var(--pt-border, rgba(0,0,0,0.08)); }
    .pt-header-sol { display: flex; align-items: center; gap: 8px; }
    .pt-header-ikon { font-size: 15px; color: var(--pt-accent, #1890ff); }
    .pt-baslik { font-weight: 600; font-size: 13px; color: var(--pt-text, rgba(0,0,0,0.88)); }
    .pt-sablon-tag { margin: 0; font-size: 11px; }
    .pt-sutun-bilgi { display: flex; flex-wrap: wrap; gap: 4px; padding: 8px 14px; flex-shrink: 0; background: var(--pt-sutun-bg, rgba(0,0,0,0.02)); border-bottom: 1px solid var(--pt-border, rgba(0,0,0,0.06)); }
    .pt-sutun-chip { margin: 0; font-size: 11px; }
    .pt-bos-durum { padding: 40px 20px; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; }
    .pt-tablo { flex: 1; min-height: 0; }
    .pt-th-no, .pt-td-no { text-align: center !important; padding: 0 8px !important; }
    .pt-satir-no { font-size: 11px; color: var(--pt-muted, rgba(0,0,0,0.35)); font-variant-numeric: tabular-nums; }
    .pt-th-sutun { padding: 6px 8px !important; }
    .pt-th-icerik { display: flex; align-items: center; justify-content: space-between; gap: 4px; }
    .pt-th-etiket { font-size: 12px; font-weight: 600; color: var(--pt-text, rgba(0,0,0,0.85)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
    .pt-th-tip { margin: 0; font-size: 10px; padding: 0 4px; flex-shrink: 0; }
    .pt-th-islem { text-align: center !important; }
    .pt-td-hucre { padding: 3px 8px !important; vertical-align: middle !important; }
    .pt-td-islem { text-align: center !important; padding: 0 !important; white-space: nowrap; }
    .pt-td-bos { text-align: center !important; padding: 32px !important; }
    .pt-hucre-goruntu { display: flex; align-items: center; gap: 6px; min-height: 26px; }
    .pt-hucre-deger { font-size: 12px; color: var(--pt-text, rgba(0,0,0,0.85)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; }
    .pt-deger-bos { color: var(--pt-muted, rgba(0,0,0,0.28)) !important; font-style: italic; }
    .pt-renk-ornek { width: 13px; height: 13px; border-radius: 3px; flex-shrink: 0; border: 1px solid rgba(0,0,0,0.15); display: inline-block; }
    .pt-input-tam { width: 100% !important; }
    .pt-renk-grup { display: flex; align-items: center; gap: 4px; }
    .pt-renk-picker { width: 26px; height: 26px; padding: 1px; border: 1px solid rgba(0,0,0,0.15); border-radius: 4px; cursor: pointer; flex-shrink: 0; }
    .pt-renk-metin { flex: 1; min-width: 0; }
    .pt-satir-duzenle td { background: var(--pt-edit-bg, #e6f4ff) !important; transition: background 0.2s; }
    .pt-satir-yeni td { background: var(--pt-new-bg, #f6ffed) !important; }
    .pt-islem-grup { display: flex; align-items: center; justify-content: center; padding: 0 4px; }
    .pt-btn-kaydet { color: #52c41a !important; }
    .pt-btn-kaydet:hover { color: #389e0d !important; }
    .pt-btn-iptal { color: #ff4d4f !important; }
    .pt-btn-duzenle { color: var(--pt-accent, #1890ff) !important; }
    .pt-btn-sil { color: var(--pt-muted, rgba(0,0,0,0.45)) !important; }
    .pt-btn-sil:hover { color: #ff4d4f !important; }
    .pt-alt-bar { display: flex; align-items: center; justify-content: space-between; padding: 6px 14px; flex-shrink: 0; font-size: 11px; color: var(--pt-muted, rgba(0,0,0,0.4)); background: var(--pt-header-bg, #fafafa); border-top: 1px solid var(--pt-border, rgba(0,0,0,0.06)); }
    .pt-alt-bilgi { display: flex; align-items: center; gap: 5px; }
    .pt-alt-sayac { font-variant-numeric: tabular-nums; }
    :host-context(body.tema-karanlik) {
      --pt-bg: #141414; --pt-border: rgba(255,255,255,0.1); --pt-header-bg: rgba(255,255,255,0.03);
      --pt-sutun-bg: rgba(255,255,255,0.02); --pt-text: rgba(255,255,255,0.85); --pt-muted: rgba(255,255,255,0.3);
      --pt-accent: #177ddc; --pt-edit-bg: rgba(24,144,255,0.08); --pt-new-bg: rgba(82,196,26,0.08);
    }
  `,
})
export class TeParametreTablosuComponent {
  sablon = input<ISablon>();
  veriler = input<Record<string, any>[]>([]);

  veriGuncellendi = output<Record<string, any>[]>();

  satirlar = signal<ISatir[]>([]);

  sutunlar = computed<ISutun[]>(() => {
    const s = this.sablon();
    if (!s?.elementler) return [];
    const gorulen = new Set<string>();
    const result: ISutun[] = [];
    for (const el of s.elementler) {
      const anahtar = el.parametreAnahtari;
      if (!anahtar || gorulen.has(anahtar)) continue;
      gorulen.add(anahtar);
      result.push({
        anahtar,
        baslik: anahtar,
        tur: this.elTipBelirle(el),
        element: el,
        genislik: this.elGenislikBelirle(el),
      });
    }
    return result;
  });

  scrollX = computed(() => {
    const w = 44 + this.sutunlar().length * 150 + 88;
    return `${Math.max(w, 500)}px`;
  });

  constructor() {
    effect(() => {
      const v = this.veriler();
      this.satirlar.set(
        v.map((veri, i) => ({
          _idx: i,
          _duzenle: false,
          _taslak: { ...veri },
          _yeni: false,
          veri: { ...veri },
        }))
      );
    }, { allowSignalWrites: true });
  }

  private elTipBelirle(el: ISablonElement): 'text' | 'number' | 'color' {
    if (SAYI_TURLER.includes(el.tur)) return 'number';
    const ak = (el.parametreAnahtari || '').toLowerCase();
    if (RENK_ANAHTAR.some(r => ak.includes(r))) return 'color';
    return 'text';
  }

  private elGenislikBelirle(el: ISablonElement): string {
    const tur = this.elTipBelirle(el);
    if (tur === 'color') return '160px';
    if (tur === 'number') return '120px';
    return '150px';
  }

  tipRengi(tur: 'text' | 'number' | 'color'): string {
    if (tur === 'number') return 'blue';
    if (tur === 'color') return 'purple';
    return 'default';
  }

  tipIkon(tur: 'text' | 'number' | 'color'): string {
    if (tur === 'number') return 'number';
    if (tur === 'color') return 'bg-colors';
    return 'font-size';
  }

  duzenle(satir: ISatir) {
    satir._taslak = { ...satir.veri };
    satir._duzenle = true;
    this.satirlar.update(list => [...list]);
  }

  kaydet(satir: ISatir) {
    satir.veri = { ...satir._taslak };
    satir._duzenle = false;
    satir._yeni = false;
    this.satirlar.update(list => [...list]);
    this.yayinla();
  }

  iptal(satir: ISatir) {
    if (satir._yeni) {
      this.satirlar.update(list => list.filter(s => s._idx !== satir._idx));
    } else {
      satir._taslak = { ...satir.veri };
      satir._duzenle = false;
      this.satirlar.update(list => [...list]);
    }
  }

  sil(idx: number) {
    this.satirlar.update(list =>
      list.filter(s => s._idx !== idx).map((s, i) => ({ ...s, _idx: i }))
    );
    this.yayinla();
  }

  satirEkle() {
    const bos: Record<string, any> = {};
    for (const s of this.sutunlar()) {
      bos[s.anahtar] = s.tur === 'number' ? 0 : '';
    }
    const yeni: ISatir = {
      _idx: Date.now(),
      _duzenle: true,
      _taslak: { ...bos },
      _yeni: true,
      veri: { ...bos },
    };
    this.satirlar.update(list => [...list, yeni]);
  }

  private yayinla() {
    this.veriGuncellendi.emit(this.satirlar().map(s => ({ ...s.veri })));
  }
}
